import React, { useEffect, useState, useRef } from 'react';
import { blockchainService } from '../../../services/blockchainService';
import D3VisualComponent from './D3VisualComponent';
import { jsPDF } from 'jspdf';
import { PDFDocument } from 'pdf-lib';
import * as d3 from 'd3';

const VisualComponent = ({ currentAddress }) => {

    const [addressNumber, setAddressNumber] = useState(3);
    const [fetchedAddresses, setFetchedAddresses] = useState([]);
    const [links, setLinks] = useState([]);
    const [nodes, setNodes] = useState([]);
    const [resultData, setResultData] = useState({});

    const isMounted = useRef(false);

    const delay = ms => new Promise(res => setTimeout(res, ms));

    const [isLoading, setIsLoading] = useState(false);

    const canvasRef = useRef(null);

    const initialize = async () => {
        if (!currentAddress) {
            console.error("No current address provided.");
            return;
        }
        setIsLoading(true);
        clear();
        const newResultData = await findAddressTransactions(currentAddress, []);
        const { nodes: newNodes, links: newLinks } = await formatData(newResultData);
        setNodes(newNodes);
        setLinks(newLinks);
        setIsLoading(false);
    }

    useEffect(() => {
        if (isMounted.current) {
            initialize();
        } else {
            isMounted.current = true;
        }
    }, [currentAddress]);

    const clear = () => {
        setResultData({});
        setFetchedAddresses([]);
    }

    const findAddressTransactions = async (address, currentFetchedAddresses) => {

        let localResultData = {};

        const localFetchedSet = new Set(currentFetchedAddresses);

        const fetchRecursively = async (addr) => {
            if (localFetchedSet.size >= addressNumber) {
                return;
            }

            if (localFetchedSet.has(addr)) {
                return;
            }
            localFetchedSet.add(addr);

            try {
                const { result } = await blockchainService.makeEthereumCall({
                    module: 'account',
                    action: 'txlist',
                    address: addr
                });

                if (!result || !Array.isArray(result) || result.length === 0) {
                    return;
                }

                const transactionsFromAddress = result.filter(
                    (transaction) => transaction.from && transaction.from.toUpperCase() === addr.toUpperCase()
                );

                localResultData = {
                    ...localResultData,
                    [addr]: transactionsFromAddress
                };

                for (const transaction of transactionsFromAddress) {
                    if (transaction.to && !localFetchedSet.has(transaction.to)) {
                        await fetchRecursively(transaction.to);
                    }
                }
            } catch (error) {
                console.error('API call failed for address:', addr, error);
            }
        };

        await fetchRecursively(address);

        setFetchedAddresses(Array.from(localFetchedSet));
        setResultData(localResultData);

        return localResultData;
    };

    const formatData = async (visualData) => {
        const nodes = [];
        const links = [];
        const addedNodes = new Set();

        Object.entries(visualData).forEach(([address, transactions]) => {
            if (!addedNodes.has(address)) {
                nodes.push({ id: address, label: formatAddress(address) });
                addedNodes.add(address);
            }

            transactions.forEach(tx => {
                const toAddress = tx.to;
                if (!addedNodes.has(toAddress)) {
                    nodes.push({ id: toAddress, label: formatAddress(toAddress) });
                    addedNodes.add(toAddress);
                }

                links.push({
                    source: address,
                    target: toAddress,
                    value: tx.value,
                    type: tx.from === address ? 'send' : 'receive'
                });
            });
        });

        return { nodes, links };
    }

    const formatAddress = (input) => {
        return input.substring(0, 6) + '...' + input.substring(input.length - 4);
    }

    const handleFormSubmit = (event) => {
        event.preventDefault();
        initialize();
    }
 
const handleExportPdf = async () => {
    if (!nodes || nodes.length === 0) {
        console.error('No graph data to export.');
        return;
    }

    const xValues = nodes.map(d => d.x);
    const yValues = nodes.map(d => d.y);
    const minX = Math.min(...xValues);
    const maxX = Math.max(...xValues);
    const minY = Math.min(...yValues);
    const maxY = Math.max(...yValues);

    const graphWidth = maxX - minX;
    const graphHeight = maxY - minY;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = graphWidth + 50;
    tempCanvas.height = graphHeight + 50;
    const tempContext = tempCanvas.getContext('2d');
    
    const tempTransform = d3.zoomIdentity
        .translate(-minX + 25, -minY + 25)
        .scale(1);
    
    tempContext.save();
    tempContext.translate(tempTransform.x, tempTransform.y);
    tempContext.scale(tempTransform.k, tempTransform.k);
    
    const linkValueScale = d3.scaleLinear().domain(d3.extent(links, d => +d.value)).range([1, 10]);
    
    links.forEach(link => {
        tempContext.beginPath();
        tempContext.moveTo(link.source.x, link.source.y);
        tempContext.lineTo(link.target.x, link.target.y);
        tempContext.strokeStyle = link.type === 'send' ? '#FF8C00' : '#32CD32';
        tempContext.globalAlpha = 0.7;
        tempContext.lineWidth = linkValueScale(+link.value);
        tempContext.stroke();
    });

    nodes.forEach(node => {
        tempContext.beginPath();
        tempContext.arc(node.x, node.y, 10, 0, 2 * Math.PI);
        tempContext.fillStyle = "#4682B4";
        tempContext.globalAlpha = 1.0;
        tempContext.fill();
        tempContext.font = "12px sans-serif";
        tempContext.fillStyle = "black";
        tempContext.fillText(node.label, node.x + 12, node.y);
    });

    tempContext.restore();

    const graphImage = tempCanvas.toDataURL('image/png', 1.0);

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Add the template image as a background
    const templateImage = await new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.src = '/template.jpg';
    });

    // Add template as a full-page background image
    doc.addImage(templateImage, 'JPG', 0, 0, pageWidth, pageHeight);

    const contentMargin = 20;
    const contentTopOffset = 40;

    doc.setFontSize(16);
    doc.text(`Analysis for Address: ${currentAddress}`, contentMargin, contentTopOffset);

    const graphAreaX = contentMargin;
    const graphAreaY = contentTopOffset + 10;
    const graphAreaWidth = pageWidth - 2 * contentMargin;
    const graphAreaHeight = pageHeight - graphAreaY - contentMargin;

    const imgAspectRatio = tempCanvas.width / tempCanvas.height;
    let imgWidth = graphAreaWidth;
    let imgHeight = graphAreaWidth / imgAspectRatio;

    if (imgHeight > graphAreaHeight) {
        imgHeight = graphAreaHeight;
        imgWidth = graphAreaHeight * imgAspectRatio;
    }

    const graphX = graphAreaX + (graphAreaWidth - imgWidth) / 2;
    const graphY = graphAreaY + (graphAreaHeight - imgHeight) / 2;

    doc.addImage(graphImage, 'PNG', graphX, graphY, imgWidth, imgHeight);

    doc.save(`analysis_${currentAddress}.pdf`);
};
    return (
        <div>
            <form onSubmit={handleFormSubmit} className="visual-form">
                <div className="input-container">
                    <label htmlFor="addressNumber" className="form-label">Number of Addresses:</label>
                    <input
                        id="addressNumber"
                        type="number"
                        value={addressNumber}
                        onChange={(e) => setAddressNumber(parseInt(e.target.value))}
                        min="1"
                        className="styled-input"
                    />
                </div>
                <button type="submit" className="styled-button" disabled={isLoading}>
                    {isLoading ? 'Loading...' : 'Submit'}
                </button>
                 <button 
                    type="button" 
                    className="styled-button" 
                    onClick={handleExportPdf} 
                    disabled={isLoading || nodes.length === 0}
                >
                    Export PDF
                </button>
            </form>
            <div className="visual">
                <D3VisualComponent nodes={nodes} links={links} selectedAddress={currentAddress} canvasRef={canvasRef} />
            </div>
        </div>
    )
};

export default VisualComponent;